// Global Instructions Rule Applied!
import { useState, useEffect, useCallback } from 'react'
import { API_ROUTES, buildFullUrl, getBackendBaseUrl } from '../utils/endpoints.ts'
import { getAccessToken } from '../auth/authService'
import { store } from '../../state_manager/store.ts'

/** @type {import('../../state_manager/data_models/select_list_entry.ts').SelectListEntry[]} */
const NO_CONTACTS = []

// Backend API configuration
const BACKEND_BASE_URL = getBackendBaseUrl()

/**
 * Custom hook for contact typeahead search with debouncing
 *
 * @param {number} debounceMs - Debounce delay in milliseconds (default: 300)
 * @returns the hook API — searchContacts, loading, error, results, clearResults, hasSearched (shape inferred from the returned literal)
 */
export const useContactSearch = (debounceMs = 300) => {
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [results, setResults] = useState(NO_CONTACTS)
  const [searchTerm, setSearchTerm] = useState('')
  const [hasSearched, setHasSearched] = useState(false)

  // Debounced search function
  const searchContacts = useCallback(
    async (query) => {
      const trimmedQuery = query?.trim() || ''
      setSearchTerm(trimmedQuery)

      // Clear results if query is empty or too short
      if (!trimmedQuery || trimmedQuery.length < 2) {
        setResults([])
        setLoading(false)
        setError(null)
        setHasSearched(false)
        return
      }

      setLoading(true)
      setError(null)

      try {
        const currentToken = getAccessToken(store.getState().auth)
        const headers = {
          'Content-Type': 'application/json'
        }

        if (currentToken) {
          headers['Authorization'] = `Bearer ${currentToken}`
        }

        const searchParams = new URLSearchParams({
          search: trimmedQuery,
          limit: '20'
        })

        const response = await fetch(
          buildFullUrl(
            BACKEND_BASE_URL,
            API_ROUTES.CONTACTS.SEARCH,
            {},
            {
              search: trimmedQuery,
              limit: '20'
            }
          ),
          {
            method: API_ROUTES.CONTACTS.SEARCH.method,
            headers
          }
        )

        if (!response.ok) {
          if (response.status === 401) {
            throw new Error('Authentication failed - please log in again')
          }
          throw new Error(`Failed to search contacts: ${response.status} ${response.statusText}`)
        }

        const result = await response.json()

        // Only update results if this is still the current search term
        if (trimmedQuery === searchTerm || searchTerm === '') {
          setResults(result.data || [])
          setHasSearched(true)
        }
      } catch (error) {
        console.error('Error searching contacts:', error)
        setError(error.message || 'An unexpected error occurred while searching contacts')
        setResults([])
        setHasSearched(true)
      } finally {
        setLoading(false)
      }
    },
    [searchTerm]
  )

  // Debounced search effect
  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchTerm) {
        searchContacts(searchTerm)
      }
    }, debounceMs)

    return () => clearTimeout(timer)
  }, [searchTerm, searchContacts, debounceMs])

  // Clear results function
  const clearResults = useCallback(() => {
    setResults([])
    setSearchTerm('')
    setError(null)
    setLoading(false)
    setHasSearched(false)
  }, [])

  // Manual search trigger (for immediate search without waiting for debounce)
  const triggerSearch = useCallback(
    (query) => {
      setSearchTerm(query)
      searchContacts(query)
    },
    [searchContacts]
  )

  return {
    searchContacts: triggerSearch,
    loading,
    error,
    results,
    clearResults,
    hasSearched
  }
}
